import { useEffect, useState } from "react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import type { VenueSponsoredAd } from "@/hooks/useVenueSponsoredAd";
import { loadSponsoredAdIndex } from "@/lib/sponsoredAdsCache";

/**
 * Returns the most recent active sponsored ad linked directly to the given
 * user profile, or null. Non-admins only see published (`is_public`) ads.
 * Shares one cached query across every profile shown on the page.
 */
export const useUserSponsoredAd = (userId: string | null | undefined) => {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [ad, setAd] = useState<VenueSponsoredAd | null>(null);
  const [loading, setLoading] = useState(!!userId);

  useEffect(() => {
    if (!userId || adminLoading) {
      setAd(null);
      setLoading(!!userId && adminLoading);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadSponsoredAdIndex(isAdmin).then((index) => {
      if (cancelled) return;
      setAd(index.byUser.get(userId) ?? null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, isAdmin, adminLoading]);

  return { ad, loading };
};
